import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_floral_blush_001',
  name: 'Elegant Floral Blush',
  occasionType: 'BABY_SHOWER',
  description: 'A premium, modern luxury baby shower invitation featuring soft sage abstract watercolor, clean white background, and fine warm gold details.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'modern', 'luxury', 'sage', 'gold', 'minimalist'],
  colorScheme: { primary: '#3F564A', accent: '#d4af37', bg: '#FDFBF7' },
};

export function ElegantFloralBlush({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables
  const motherName = baby.motherName || 'Emma Harrington';
  const hostedBy = baby.hostedBy || data.hostName || 'The Harrington Family';
  
  // Format Date
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'August 4, 2026';
  let dayOfWeek = 'Tuesday';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' });
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '04:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'The Stardust Pavilion';
  const venueAddress = baby.venueAddress || data.eventVenue || '555 Celestial Way, San Francisco, CA';
  
  const message = baby.message || data.customMessage || 'Love you to the moon and back. Join us to celebrate the upcoming arrival of our little star!';
  const dressCode = baby.dressCode || 'Champagne Gold or Warm White';

  return (
    <div className="min-h-screen bg-[#F0F2F0] py-12 px-4 flex flex-col items-center justify-start font-sans relative overflow-x-hidden">
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=Montserrat:wght@300;400;500;600;700&family=Great+Vibes&display=swap');
        .font-serif-luxury { font-family: 'Cormorant Garamond', Georgia, serif; }
        .font-sans-luxury { font-family: 'Montserrat', sans-serif; }
        .font-script-luxury { font-family: 'Great Vibes', cursive; }
        .gold-text {
          background: linear-gradient(135deg, #A87C35 0%, #D4AF37 50%, #A87C35 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      `}} />

      <div className="w-full max-w-[800px] flex justify-center origin-top transform sm:scale-100 scale-[0.45] md:scale-[0.75] lg:scale-100 transition-transform">
        
        {/* Master Canvas Container - Premium warm white luxury paper background */}
        <div className="w-[800px] bg-[#FCFAF6] shadow-2xl relative flex flex-col items-center overflow-hidden shrink-0 pb-[90px] min-h-[1200px]">
          
          {/* ====================================================
              ABSOLUTE DECORATIVE ELEMENTS
             ==================================================== */}
          
          {/* Fine Noise Texture */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.15] mix-blend-multiply z-0">
            <filter id="noise">
              <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" stitchTiles="stitch" />
              <feColorMatrix type="matrix" values="1 0 0 0 0, 0 1 0 0 0, 0 0 1 0 0, 0 0 0 0.08 0" />
            </filter>
            <rect width="100%" height="100%" filter="url(#noise)" />
          </svg>



          {/* Top Left Arch (Triple Nested + Heart) - Aligned with BABY SHOWER text */}
          <div className="absolute top-[310px] left-[65px] z-10 flex flex-col items-center">
            <svg width="40" height="50" viewBox="0 0 60 70" fill="none">
              <path d="M 5 70 L 5 30 A 25 25 0 0 1 55 30 L 55 70" stroke="#C5A059" strokeWidth="1" />
              <path d="M 15 70 L 15 30 A 15 15 0 0 1 45 30 L 45 70" stroke="#C5A059" strokeWidth="1" />
              <path d="M 25 70 L 25 30 A 5 5 0 0 1 35 30 L 35 70" stroke="#C5A059" strokeWidth="1" />
              {/* Little Heart inside the Arch */}
              <path d="M 30 50 C 30 50 24 44 24 38 C 24 34 30 34 30 38 C 30 34 36 34 36 38 C 36 44 30 50 30 50 Z" fill="#C5A059" />
            </svg>
          </div>

          {/* Top Watercolor Wave (Dark Sage + Gold Lines) - Expanded past borders & corners */}
          <div className="absolute top-0 left-[-100px] w-[1000px] h-[360px] pointer-events-none z-0 overflow-hidden">
            <div 
               className="absolute top-0 left-0 w-full h-full opacity-[0.85] mix-blend-multiply"
               style={{
                 filter: 'contrast(1.15) brightness(1.05)',
                 WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)',
                 maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)'
               }}
             >
               <img src="/assets/images/templates/floral-blush/top_watercolor.jpg?v=4" alt="" className="w-full h-full object-cover object-center scale-[1.25] origin-top" />
             </div>
          </div>

          {/* Hanging Ornaments (Exactly matching reference - aligned to inner border top Y=28px) */}
          <div className="absolute top-[28px] right-[40px] w-[500px] h-[350px] pointer-events-none z-10">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 500 350" fill="none">
              {/* Ornament 1 */}
              <line x1="50" y1="0" x2="50" y2="180" stroke="#C5A059" strokeWidth="1" />
              <circle cx="50" cy="185" r="4" fill="#C5A059" />
              {/* Ornament 2 */}
              <line x1="120" y1="0" x2="120" y2="240" stroke="#C5A059" strokeWidth="1" />
              <circle cx="120" cy="244" r="4" fill="#C5A059" />
              {/* Ornament 3 */}
              <line x1="190" y1="0" x2="190" y2="120" stroke="#C5A059" strokeWidth="1" />
              <circle cx="190" cy="125" r="4" fill="#C5A059" />
              {/* Ornament 4 */}
              <line x1="250" y1="0" x2="250" y2="160" stroke="#C5A059" strokeWidth="1" />
              <path d="M250,160 Q260,170 250,180 Q240,170 250,160 Z" fill="#C5A059" opacity="0.8" />
              {/* Ornament 5 */}
              <line x1="310" y1="0" x2="310" y2="280" stroke="#C5A059" strokeWidth="1" />
              <polygon points="310,280 318,292 310,304 302,292" fill="#C5A059" />
              {/* Ornament 6 (Ring with leaf) */}
              <line x1="400" y1="0" x2="400" y2="200" stroke="#C5A059" strokeWidth="1" />
              <circle cx="400" cy="225" r="25" stroke="#C5A059" strokeWidth="1.5" fill="none" />
              <path d="M390,240 C380,220 400,210 400,210 C400,210 420,220 410,240 Z" fill="#758277" opacity="0.8" />
              <path d="M395,225 C390,210 400,205 400,205 C400,205 410,210 405,225 Z" fill="#4B5B53" opacity="0.8" />
            </svg>
          </div>

          {/* Bottom Right Dark Wave */}
          <div className="absolute bottom-0 right-0 w-[500px] h-[400px] pointer-events-none z-0">
             {/* Hand-painted Botanical Leaves Masked into the Corner */}
             <div 
               className="absolute bottom-[-100px] right-[-100px] w-[550px] h-[550px] opacity-[0.25] mix-blend-multiply"
               style={{
                 filter: 'contrast(1.12) brightness(1.08)',
                 WebkitMaskImage: 'radial-gradient(circle at 80% 80%, black 20%, transparent 60%)',
                 maskImage: 'radial-gradient(circle at 80% 80%, black 20%, transparent 60%)'
               }}
             >
               <img src="/assets/images/templates/floral-blush/bottom_right.jpg?v=1" alt="" className="w-full h-full object-cover transform -rotate-12" />
             </div>
          </div>

          {/* Bottom Left Watercolor Wash (Behind Mother) */}
          <div className="absolute bottom-0 left-0 w-[450px] h-[350px] pointer-events-none z-10">
             <div 
               className="absolute top-0 left-0 w-full h-full opacity-[0.22] mix-blend-multiply"
               style={{
                 filter: 'contrast(1.12) brightness(1.08)',
                 WebkitMaskImage: 'radial-gradient(circle at 20% 80%, black 30%, transparent 75%)',
                 maskImage: 'radial-gradient(circle at 20% 80%, black 30%, transparent 75%)'
               }}
             >
               <img src="/assets/images/templates/floral-blush/bottom_left.jpg?v=1" alt="" className="w-full h-full object-cover object-left-bottom" />
             </div>
          </div>

          {/* Bottom Illustration (Mother) - Corrected sizing & bottom corner placement */}
          <div 
            className="absolute bottom-[55px] left-[30px] w-[190px] h-[300px] pointer-events-none z-10 mix-blend-multiply"
            style={{ 
              filter: 'contrast(1.05) brightness(1.02)'
            }}
          >
            <img 
              src="/assets/images/templates/floral-blush/mother.jpg?v=5" 
              alt="Mother illustration"
              className="w-full h-full object-contain object-bottom"
              style={{
                WebkitMaskImage: 'radial-gradient(ellipse 100% 100% at 50% 100%, black 80%, transparent 100%)',
                maskImage: 'radial-gradient(ellipse 100% 100% at 50% 100%, black 80%, transparent 100%)'
              }}
            />
          </div>

          {/* Gold Foil Borders & Accents (Renders ON TOP of paintings) */}          <div className="absolute inset-0 pointer-events-none z-20">
            {/* Main Double Border */}
            <div className="absolute inset-[15px] border-[1px] border-[#C5A059] opacity-80 pointer-events-none"></div>
            <div className="absolute inset-[22px] border-[1px] border-[#C5A059] opacity-60 pointer-events-none"></div>
            
            {/* Corner dots */}
            <div className="absolute top-[18px] left-[18px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
            <div className="absolute top-[18px] right-[18px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
            <div className="absolute bottom-[18px] left-[18px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
            <div className="absolute bottom-[18px] right-[18px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
            
            {/* Inner corner tiny dots */}
            <div className="absolute top-[4px] left-[4px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
            <div className="absolute top-[4px] right-[4px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
            <div className="absolute bottom-[4px] left-[4px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
            <div className="absolute bottom-[4px] right-[4px] w-[3px] h-[3px] bg-[#C5A059] rounded-full"></div>
          </div>

          {/* Ambient Gold Splatters */}
          <div className="absolute inset-0 w-full h-full pointer-events-none z-0">
             <svg className="w-full h-full opacity-60" viewBox="0 0 800 1200" fill="#C5A059">
                <circle cx="100" cy="400" r="2.5" />
                <circle cx="150" cy="350" r="1.5" />
                <circle cx="700" cy="500" r="2" />
                <circle cx="650" cy="650" r="1.5" />
                <circle cx="750" cy="800" r="2.5" />
                <circle cx="70" cy="750" r="2" />
                <circle cx="120" cy="850" r="1.5" />
                <circle cx="720" cy="1000" r="2" />
             </svg>
          </div>


          {/* ====================================================
              VERTICAL FLOW CONTENT 
             ==================================================== */}
          
          <div className="w-full max-w-[660px] flex flex-col items-center relative z-20 pt-[240px]">
            
            {/* Hero */}
            <span className="font-sans-luxury text-[11px] tracking-[0.2em] text-[#364940] font-bold mb-[8px]">
              PLEASE JOIN US FOR A
            </span>

            <h2 
              className="font-serif-luxury text-[#364940] font-bold text-center mb-[12px] uppercase"
              style={{
                fontSize: '56px',
                lineHeight: '60px',
                letterSpacing: '0.03em'
              }}
            >
              BABY<br/>SHOWER
            </h2>

            <svg width="10" height="10" viewBox="0 0 24 24" fill="#C5A059" className="mb-[10px]">
               <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>

            <div className="flex items-center justify-center gap-4 w-full mb-[20px]">
              <div className="h-[1px] bg-[#C5A059] w-[60px]"></div>
              <span className="font-sans-luxury text-[10px] text-[#A87C35] uppercase tracking-[0.2em] font-bold">
                HONORING THE MOTHER-TO-BE
              </span>
              <div className="h-[1px] bg-[#C5A059] w-[60px]"></div>
            </div>

            {/* Mother Name */}
            <h1 
              className="font-script-luxury gold-text font-normal text-center w-full max-w-[520px] mb-[10px] break-words"
              style={{
                fontSize: '64px',
                lineHeight: '70px'
              }}
            >
              {motherName}
            </h1>
            
            <svg width="10" height="10" viewBox="0 0 24 24" fill="#C5A059" className="mb-[20px]">
               <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>

            {/* Hosted By */}
            <p className="font-sans-luxury text-[10px] tracking-[0.25em] uppercase text-[#364940] font-bold text-center mb-[5px]">
              HOSTED WITH LOVE BY
            </p>
            <span className="font-serif-luxury text-[18px] text-[#364940] tracking-[0.05em] font-medium text-center block mb-[15px]">
              {hostedBy}
            </span>

            {/* Elegant Divider */}
            <div className="flex items-center justify-center w-full max-w-[500px] mb-[30px]">
              <div className="w-[4px] h-[4px] rounded-full bg-[#C5A059]"></div>
              <div className="h-[1px] bg-[#C5A059] w-full max-w-[220px]"></div>
              <div className="w-[6px] h-[6px] transform rotate-45 bg-[#C5A059] mx-2"></div>
              <div className="h-[1px] bg-[#C5A059] w-full max-w-[220px]"></div>
              <div className="w-[4px] h-[4px] rounded-full bg-[#C5A059]"></div>
            </div>

            {/* Information Row */}
            <div className="w-full flex justify-between items-start mb-[35px] px-[20px]">
              {/* Date */}
              <div className="flex flex-col items-center flex-1 border-r-[1px] border-[#C5A059]/30 h-full min-h-[75px] justify-start">
                <svg className="w-[22px] h-[22px] mb-[8px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="#C5A059" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                  <rect x="7" y="14" width="2" height="2" fill="#C5A059" stroke="none"/>
                  <rect x="11" y="14" width="2" height="2" fill="#C5A059" stroke="none"/>
                  <rect x="15" y="14" width="2" height="2" fill="#C5A059" stroke="none"/>
                </svg>
                <span className="font-sans-luxury text-[9px] uppercase tracking-[0.1em] text-[#364940] font-bold mb-[2px]">{dayOfWeek}</span>
                <span className="font-sans-luxury text-[9px] uppercase tracking-[0.1em] text-[#333333] font-bold text-center block leading-tight">{formattedDate}</span>
              </div>

              {/* Time */}
              <div className="flex flex-col items-center flex-1 border-r-[1px] border-[#C5A059]/30 h-full min-h-[75px] justify-start">
                <svg className="w-[22px] h-[22px] mb-[8px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="#C5A059" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <span className="font-sans-luxury text-[9px] uppercase tracking-[0.1em] text-[#364940] font-bold mb-[2px]">AT {eventTime}</span>
                <span className="font-sans-luxury text-[9px] uppercase tracking-[0.1em] text-[#333333] font-bold text-center block leading-tight">ONWARDS</span>
              </div>

              {/* Venue */}
              <div className="flex flex-col items-center flex-1 h-full min-h-[75px] justify-start">
                <svg className="w-[22px] h-[22px] mb-[8px] shrink-0" viewBox="0 0 24 24" fill="none" stroke="#C5A059" strokeWidth="1.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span className="font-sans-luxury text-[9px] uppercase tracking-[0.1em] text-[#364940] font-bold mb-[2px]">{venueName}</span>
                <span className="font-sans-luxury text-[9px] uppercase tracking-[0.05em] text-[#333333] font-bold mt-[1px] px-2 text-center block leading-snug max-w-[180px]">{venueAddress}</span>
              </div>
            </div>

            {/* Dress Code */}
            {dressCode && (
              <div className="relative w-full max-w-[360px] h-[56px] mb-[30px] bg-[#F3F1EC]/90 flex items-center justify-center mx-auto shadow-sm rounded-[4px] border border-[#C5A059]/30">
                <div className="absolute inset-[3px] border-[1px] border-[#C5A059]/50 rounded-[2px]"></div>
                
                {/* Bow icon top center */}
                <div className="absolute top-[-10px] bg-[#F4F3ED] px-[10px]">
                   <svg width="22" height="16" viewBox="0 0 24 24" fill="none" stroke="#C5A059" strokeWidth="1.5">
                      <path d="M12 12C9 7 4 7 4 11C4 16 12 18 12 18C12 18 20 16 20 11C20 7 15 7 12 12Z" />
                      <circle cx="12" cy="12" r="1.5" fill="#C5A059" />
                   </svg>
                </div>
                
                {/* Side Diamonds */}
                <div className="absolute left-[12px] w-[4px] h-[4px] rotate-45 bg-[#C5A059]"></div>
                <div className="absolute right-[12px] w-[4px] h-[4px] rotate-45 bg-[#C5A059]"></div>
                
                <div className="flex flex-col items-center justify-center z-10">
                  <span className="font-sans-luxury text-[9px] tracking-[0.2em] text-[#364940] uppercase font-bold mb-[2px]">
                    DRESS CODE:
                  </span>
                  <span className="font-sans-luxury text-[10px] tracking-[0.1em] text-[#333333] uppercase font-bold">
                    {dressCode}
                  </span>
                </div>
              </div>
            )}

            {/* Message / Quote */}
            {message && (
              <div className="w-full max-w-[340px] mb-[25px] flex flex-col items-center">
                {/* Inline-Block Wrapper for Perfect Quotation Marks Bracketing */}
                <div className="relative inline-block px-8 max-w-[280px]">
                  <span className="absolute top-[-10px] left-[-5px] text-[40px] font-serif-luxury text-[#C5A059] leading-none select-none">“</span>
                  <span className="absolute bottom-[-22px] right-[-5px] text-[40px] font-serif-luxury text-[#C5A059] leading-none select-none">”</span>
                  
                  <p className="font-serif-luxury text-[14px] text-[#333333] italic leading-[22px] text-center font-medium">
                    “{message}”
                  </p>
                </div>
                
                <svg width="10" height="10" viewBox="0 0 24 24" fill="#C5A059" className="mt-[15px]">
                   <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
              </div>
            )}

            {/* RSVP Button */}
            <div className="w-full flex justify-center mb-[25px]">
              <button
                type="button"
                onClick={() => setShowRsvpModal(true)}
                className="inline-flex items-center justify-center gap-3 w-[280px] h-[48px] rounded-[24px] bg-[#364940] hover:bg-[#2A3C31] text-white font-serif-luxury text-[16px] uppercase tracking-[0.15em] shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <span className="text-[#C5A059] text-[18px] font-sans">♡</span>
                <span className="pt-[1px] text-white">RSVP NOW</span>
                <span className="text-[#C5A059] text-[18px] font-sans">♡</span>
              </button>
            </div>

            {/* Footer */}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="#C5A059" className="mb-[12px]">
               <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
            </svg>
            <div className="w-full flex items-center justify-center gap-4 pb-[100px]">
              <div className="flex items-center">
                 <div className="w-[30px] h-[1px] bg-[#C5A059]"></div>
                 <div className="w-[4px] h-[4px] rotate-45 bg-[#C5A059]"></div>
              </div>
              <span className="font-sans-luxury text-[10px] uppercase tracking-[0.15em] text-[#333333] font-bold text-center">
                We can't wait to celebrate with you!
              </span>
              <div className="flex items-center">
                 <div className="w-[4px] h-[4px] rotate-45 bg-[#C5A059]"></div>
                 <div className="w-[30px] h-[1px] bg-[#C5A059]"></div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-[#333333]/80 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          <div className="relative w-full max-w-md bg-[#FAFAFA] border border-[#C5A059]/20 rounded-sm p-8 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-[#7A7A7A] hover:text-[#333333] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="text-center mb-8">
              <h3 className="text-3xl font-serif-luxury text-[#333333] font-normal italic">Are You Attending?</h3>
              <p className="font-sans-luxury text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mt-3">Please RSVP to let us know if you're coming</p>
            </div>
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: ElegantFloralBlush });
